<template>
  <div class="cash-out-management">
    <TableList
      v-rect-loading="loading"
      noAction
      noHeadBtn
      showSearchFilter
      row-key="id"
      class="cash-out-list"
      :showCreateBtn="false"
      :title="$t(`${i18nPrefix}.title.cashOutManagement`)"
      :listData="rewardList"
      :tableColumns="tableColumns"
      :pagination="pagination"
      @pagination-change="handlePaginationChange"
      @select-all="handleSelectAll"
      @selection-change="handleSelectionChange"
    >
      <template #filter>
        <el-select v-model="search.is_allocate" class="allocate-select" @change="handleSearch">
          <el-option
          v-for="(item, index) in allocateOptionList"
          :key="index"
          :value="item.value"
          :label="item.label"
          ></el-option>
        </el-select>
        <div class="date-select">
          <el-date-picker
            v-model="startEndTimeList"
            type="daterange"
            align="right"
            value-format="yyyy-MM-dd"
            prefix-icon="el-icon-time"
            range-separator="-"
            :start-placeholder="$t(`${i18nPrefix}.label.startDate`)"
            :end-placeholder="$t(`${i18nPrefix}.label.endDate`)"
            :default-value="defaultDateRange"
            :picker-options="pickerOptions"
            @change="handleSearch"
          >
          </el-date-picker>
        </div>
        <div class="reward-type-class">
          <div class="label">{{ $t(`${i18nPrefix}.label.rewardType`) }}</div>
          <el-select v-model="search.reward_type" clearable @change="handleSearch">
            <el-option
              v-for="(item, index) in rewardTypeList"
              :key="index"
              :value="item.value"
              :label="item.label"
            ></el-option>
          </el-select>
        </div>
      </template>
      <template #search-filter>
        <div class="referral-total">
          <span class="label">{{ $t(`${i18nPrefix}.label.selectedReferrer`) }}</span>
          <span class="number">{{ selectTotalReferral }}</span>
        </div>
        <div class="referral-earnings-total">
          <span class="label">{{ $t(`${i18nPrefix}.label.sumOfEarnings`) }}</span>
          <span class="number">{{ sumOfEarnings }}</span>
        </div>
      </template>
      <template #search-filter-btn>
        <el-button v-if="search.is_allocate === ALLOCATE_MAP.PENDING" class="out-reward" @click="handleOutReward">{{ $t(`${i18nPrefix}.btn.outReward`) }}</el-button>
        <div v-rect-loading="exportStatus.loading" class="export" @click="handleExport">
          <div class="btn-label">{{ $t(`${i18nPrefix}.btn.export`) }}</div>
        </div>
      </template>
    </TableList>
    <DialogWrapper
      saveBtnLabelI18nKey="common.btn.confirm"
      closeOnCancel
      class="pay-reward-dialog"
      :title="$t(`${i18nPrefix}.message.confirmPayRewardTip`)"
      :loading="payRewardDialog.loading"
      :show.sync="payRewardDialog.show"
      @save="handleConfirmPayReward"
      @closed="handlePayRewardClosed"
    >
      <div class="content">{{ confirmPayRewardContent }}</div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { formatBigNumber } from '@/components/filters'
import { AsyncTask } from '@/utils/common'
import { DATE_PICKER_SHORTCUTS } from '@/constant/common'

const ALLOCATE_MAP = {
  PENDING: '1',
  TRANSFERRED: '2'
}

const REWARD_TYPE_MAP = {
  CASH: 'cash',
  POINTS: 'points',
  PAYROLL: 'payroll'
}

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      ALLOCATE_MAP,
      i18nPrefix: 'admin.company.cashOutManagement',
      loading: false,
      // 上个月第一天，当前日期
      defaultDateRange: [
        moment().subtract(1, 'month').startOf('month').toDate(),
        moment().toDate()
      ],
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      },
      search: {
        is_allocate: '1',
        start_date: null,
        end_date: null,
        reward_type: null,
        referrer_ids: [],
        is_all: null
      },
      rewardList: [],
      totalPoints: 0,
      payRewardDialog: {
        loading: false,
        show: false
      },
      exportStatus: {
        loading: false
      }
    }
  },
  computed: {
    startEndTimeList: {
      get () {
        const list = []
        if (this.search.start_date && this.search.end_date) {
          list[0] = this.search.start_date
          list[1] = this.search.end_date
        }
        return list
      },
      set (val) {
        this.search.start_date = val ? val[0] : null
        this.search.end_date = val ? val[1] : null
      }
    },
    pickerOptions () {
      return {
        shortcuts: _.map(DATE_PICKER_SHORTCUTS, (item, key) => ({
          text: this.$t(`common.dateShortcut.${key}`),
          onClick (picker) {
            const start = moment().startOf(item.unit).add(item.start).toDate()
            const end = moment().startOf(item.unit).add(item.end).toDate()
            picker.$emit('pick', [start, end])
          }
        })),
        disabledDate (time) {
          const current = new Date()
          return time.valueOf() > current.valueOf()
        }
      }
    },
    allocateOptionList () {
      return _.map(ALLOCATE_MAP, (val, key) => {
        return {
          value: val,
          label: this.$t(`${this.i18nPrefix}.ALLOCATE_MAP.${key}`)
        }
      })
    },
    rewardTypeList () {
      return _.map(REWARD_TYPE_MAP, (val, key) => {
        return {
          value: val,
          label: this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.${key}`)
        }
      })
    },
    confirmPayRewardContent () {
      let rewardType
      if (this.search.reward_type === REWARD_TYPE_MAP.CASH) {
        rewardType = this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.CASH`)
      } else if (this.search.reward_type === REWARD_TYPE_MAP.POINTS) {
        rewardType = this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.POINTS`)
      } else if (this.search.reward_type === REWARD_TYPE_MAP.PAYROLL) {
        rewardType = this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.PAYROLL`)
      }
      let text
      if (rewardType) {
        text = this.$t(`${this.i18nPrefix}.message.confirmPayRewardType`, { rewardType })
      } else {
        text = this.$t(`${this.i18nPrefix}.message.confirmPayRewardAllType`)
      }
      return `${this.$t(`${this.i18nPrefix}.message.confirmPayRewardContent`)} ${text}`
    },
    tableColumns () {
      const columns = [
        {
          columnType: 'selection',
          cellClass: 'selection-cell'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.referrer`),
          dataKey: 'referral_account',
          formatter: (val, row) => {
            if (val.email) {
              return val.email
            } else if (val.mobile) {
              return val.mobile
            } else if (val.wechat_name) {
              return val.wechat_name
            }
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.earnings`),
          dataKey: 'points',
          formatter: (val) => {
            return formatBigNumber(val)
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.rewardType`),
          dataKey: 'reward_type',
          formatter: (val) => {
            if (val === 'cash') {
              return this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.CASH`)
            } else if (val === 'points') {
              return this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.POINTS`)
            } else if (val === 'payroll') {
              return this.$t(`${this.i18nPrefix}.REWARD_TYPE_MAP.PAYROLL`)
            }
          }
        },
        {
          hidden: this.search.is_allocate !== '2',
          label: this.$t(`${this.i18nPrefix}.label.operator`),
          dataKey: 'final_grant_account.name'
        },
        {
          hidden: this.search.is_allocate !== '2',
          label: this.$t(`${this.i18nPrefix}.label.payDate`),
          dataKey: 'final_grant_date',
          formatter: (val) => {
            return moment(val).format('YYYY/MM/DD')
          }
        }
      ]
      return columns
    },
    selectTotalReferral () {
      if (this.search.is_all) {
        return formatBigNumber(this.pagination.total)
      } else {
        return formatBigNumber(this.search.referrer_ids.length)
      }
    },
    sumOfEarnings () {
      if (this.search.is_all) {
        return formatBigNumber(this.totalPoints)
      } else {
        let points = 0
        _.forEach(_.filter(this.rewardList, reward => _.includes(this.search.referrer_ids, reward.id)), reward => {
          points += reward.points
        })
        return formatBigNumber(points)
      }
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('referral', ['getReferralRewardList', 'payReward', 'createExportReferralRewardTask', 'getReferralRewardExportTaskInfo']),
    initPage () {
      const query = this.$route.query
      if (query.is_allocate) {
        this.search.is_allocate = query.is_allocate
      }
      if (query.start_date) {
        this.search.start_date = query.start_date
      }
      if (query.end_date) {
        this.search.end_date = query.end_date
      }
      if (query.page_num) {
        this.pagination.page = _.parseInt(query.page_num)
      }
      if (query.page_size) {
        this.pagination.pageSize = _.parseInt(query.page_size)
      }
      this.loadRewardList()
    },
    async loadRewardList () {
      this.loading = true
      let startDate
      let endDate
      if (this.startEndTimeList?.length) {
        startDate = this.search.start_date
        endDate = this.search.end_date
      }
      const params = {
        is_allocate: this.search.is_allocate,
        reward_type: this.search.reward_type,
        start_date: startDate,
        end_date: endDate,
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      this.setUrlByParams()
      try {
        const resp = await this.getReferralRewardList(params)
        if (resp) {
          this.rewardList = resp.list_data
          this.pagination.total = resp.total_count
          this.totalPoints = resp.total_points
        }
      } finally {
        this.loading = false
      }
    },
    setUrlByParams () {
      let startDate
      let endDate
      if (this.startEndTimeList?.length) {
        startDate = this.search.start_date
        endDate = this.search.end_date
      }
      this.$router.replace({
        query: {
          is_allocate: this.search.is_allocate,
          reward_type: this.search.reward_type,
          start_date: startDate,
          end_date: endDate,
          page_num: this.pagination.page,
          page_size: this.pagination.pageSize
        }
      })
    },
    handlePaginationChange () {
      this.loadRewardList()
    },
    handleSearch () {
      let startDate
      let endDate
      if (this.startEndTimeList?.length) {
        startDate = this.search.start_date
        endDate = this.search.end_date
      }
      if (startDate && endDate && !moment(endDate).isAfter(startDate)) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.endDateAfterStartDate`))
        return
      }
      this.search.is_all = false
      this.loadRewardList()
    },
    handleSelectAll (selection) {
      const isSelectAll = selection.length > 0
      const currentPageIds = _.map(this.rewardList, this.rowKey)

      if (isSelectAll) {
        // 全选：将当前页所有行加入 selectedRows（去重）
        this.selectedRewardList = this.rewardList
      } else {
        // 取消全选：从 selectedRows 中移除当前页的行
        this.selectedRewardList = _.filter(this.selectedRewardList, reward => !_.includes(currentPageIds, reward.id))
      }
      this.search.referrer_ids = _.map(this.selectedRewardList, 'id')
      this.search.is_all = isSelectAll
    },
    handleSelectionChange (selection) {
      const currentSelected = selection
      const currentPageIds = _.map(this.rewardList, 'id')
      // 从 selectedRewardList 中移除当前页的未选中行
      this.selectedRewardList = _.filter(this.selectedRewardList, reward => !_.includes(currentPageIds, reward.id))

      // 添加当前页的新选中行
      this.selectedRewardList = _.concat(this.selectedRewardList, currentSelected)

      this.search.referrer_ids = _.map(this.selectedRewardList, 'id')
      this.search.is_all = this.selectedRewardList.length === this.pagination.total
    },
    handleOutReward () {
      if (!this.search.referrer_ids?.length) {
        return
      }
      this.payRewardDialog.show = true
    },
    handlePayRewardClosed () {
      this.payRewardDialog.show = false
    },
    async handleConfirmPayReward () {
      let startDate
      let endDate
      if (this.startEndTimeList?.length) {
        startDate = this.search.start_date
        endDate = this.search.end_date
      }
      this.payRewardDialog.loading = true
      const params = {
        is_allocate: this.search.is_allocate,
        reward_type: this.search.reward_type,
        start_date: startDate,
        end_date: endDate,
        referrer_ids: this.search.referrer_ids.join(','),
        is_all: this.search.is_all
      }
      try {
        const resp = await this.payReward(params)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.payRewardSuccess`)
          })
          this.loadRewardList()
        }
        this.payRewardDialog.show = false
      } finally {
        this.payRewardDialog.loading = false
      }
    },
    handleExport () {
      this.exportStatus.loading = true
      const exportRewardStatus = {
        finished: false,
        task: null
      }
      exportRewardStatus.finished = false
      let startDate
      let endDate
      if (this.startEndTimeList?.length) {
        startDate = this.search.start_date
        endDate = this.search.end_date
      }
      exportRewardStatus.task = new AsyncTask(() => this.createExportReferralRewardTask({
        is_allocate: this.search.is_allocate,
        reward_type: this.search.reward_type,
        start_date: startDate,
        end_date: endDate,
        referrer_ids: this.search.referrer_ids.join(','),
        is_all: this.search.is_all
      }), (taskId) => this.getReferralRewardExportTaskInfo({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/client-admin/reward/export-excel/?task_id=${taskId}&download=1`)
        exportRewardStatus.finished = false
        exportRewardStatus.task = null
        this.exportStatus.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.cash-out-management {
  .cash-out-list {
    .selection-cell {

    }
    .allocate-select {
      flex: 1;
    }
    .date-select {
      ::v-deep {
        .el-date-editor {
          .el-range__icon {
            height: inherit;
          }
          .el-range-input {
            line-height: 30;
            height: 30px;
          }
          .el-range-separator  {
            flex: 1;
            display: inline-flex;
            height: 100%;
            justify-content: center;
            align-items: center;
          }
          .el-range__close-icon {
            height: inherit;
          }
        }
      }
    }
    .reward-type-class {
      flex: 1;
      display: flex;
      align-items: center;
      .label {
        padding: 0 10px;
      }
      .el-select {
        flex: 1;
      }
    }
    .referral-total {
      margin-right: 20px;
      .label {
        font-size: 16px;
      }
      .number {
        font-size: 16px;
      }
    }
    .referral-earnings-total {
      .label {
        font-size: 16px;
      }
      .number {
        font-size: 16px;
      }
    }
    .out-reward {
      line-height: 22px;
      color: #0096cc;
      cursor: pointer;
      &:hover {
        color: #0096cc;
      }
    }
    .export {
      line-height: 22px;
      color: #0096cc;
      cursor: pointer;
      margin-left: 20px;
      padding: 6px 14px;
      border: 1px solid #0096cc;
      border-radius: 4px;
      &:hover {
        color: #0096cc;
        background-color: #e6f5fa;
      }
      ::v-deep {
        .el-loading-mask {
          top: 2px;
          height: 28px;
          z-index: 100 !important;
        }
        .el-loading-spinner {
          margin-top: -12px;

          .loading-spinner {
            .spinner {
              height: 24px;
            }
          }
        }
      }
    }
  }
  .pay-reward-dialog {
    .content {
      word-wrap: normal;
      word-break: break-word;
    }
  }
}
</style>
